/**
 * A list of things, as a table: the scaffold eight screens were each writing
 * out by hand.
 *
 * `Paper` → `Table` → a `SortableHeadCell` for the sortable columns and a plain
 * one for the rest → the rows → `TablePager`, with `EmptyState` instead of all
 * of it when there is nothing. That is forty lines of identical markup per page,
 * and the differences between the copies were accidents rather than decisions:
 * one table's pager was missing, another's sortable column did not say so.
 *
 * ## What it is not
 *
 * **It does not fetch, and it does not know what an entity is.** It takes rows
 * and renders them, which is the rule the rest of `components/` follows. The
 * page still owns the request, the `useAsync` state and the `AsyncContent`
 * around this — because "what happens while it loads" is a data question and
 * this file has no data.
 *
 * So there is no entity registry and no `canEdit` flag here. Whether a row can
 * be edited is a permission the page already holds, and it expresses that by
 * returning an action — or not returning one — from `actions`.
 *
 * ```tsx
 * <AsyncContent state={rules}>
 *   {({ rules: rows, page }) => (
 *     <EntityTable
 *       rows={rows}
 *       rowKey={(rule) => rule.id}
 *       list={list}
 *       page={page}
 *       columns={[
 *         { field: 'name', label: t('rules.column.name'), sortable: true,
 *           render: (rule) => rule.name },
 *       ]}
 *       actions={(rule) => [...]}
 *       empty={{ title: t('rules.emptyTitle') }}
 *     />
 *   )}
 * </AsyncContent>
 * ```
 */
import type { Key, ReactNode } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { PageInfo } from '@forsys/shared';
import type { ListQueryState } from '../hooks/useListQuery';
import { EmptyState, type EmptyStateProps } from './EmptyState';
import { RowActions, type EntityAction } from './RowActions';
import { SortableHeadCell } from './SortableHeadCell';
import { TablePager } from './TablePager';

export interface EntityColumn<Row> {
  /**
   * The **API's** field name when sortable — `displayName`, not "Name" — because
   * that is what goes back to the server as `sort`. For a column that is not
   * sortable it is only a React key, so anything unique will do.
   */
  field: string;
  label: ReactNode;
  /**
   * Clickable header. Requires `list`, and the field must be one the endpoint
   * actually accepts: an allowlist miss is a 400, not a silently ignored sort.
   */
  sortable?: boolean;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  render: (row: Row) => ReactNode;
}

export interface EntityTableProps<Row> {
  rows: readonly Row[];
  rowKey: (row: Row) => Key;
  columns: readonly EntityColumn<Row>[];
  /** Shown instead of the table — header included — when there are no rows. */
  empty: EmptyStateProps;
  /**
   * Sorting and paging state, from `useListQuery`. Omit for a table that is
   * neither: the permission catalogue, a version history, a fixed short list.
   */
  list?: ListQueryState;
  /** The `page` block from the response. Omit and no pager is rendered. */
  page?: PageInfo | undefined;
  /**
   * The row's buttons. Returning `[]` — or every action `hidden` — leaves the
   * column in place but empty, so rows do not change width from row to row.
   * Omit the prop entirely and there is no action column at all.
   */
  actions?: (row: Row) => readonly EntityAction[];
  /** Width of the action column. Roughly 40px per action, plus breathing room. */
  actionsWidth?: number;
  size?: 'small' | 'medium';
  /** Rendered between the head and the first row. For a "3 selected" bar. */
  toolbar?: ReactNode;
}

export function EntityTable<Row>(props: EntityTableProps<Row>) {
  const {
    rows,
    rowKey,
    columns,
    empty,
    list,
    page,
    actions,
    actionsWidth = 112,
    size = 'small',
    toolbar,
  } = props;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {rows.length === 0 ? (
        <EmptyState {...empty} />
      ) : (
        <>
          {toolbar}
          <Table size={size}>
            <TableHead>
              <TableRow>
                {columns.map((column) =>
                  column.sortable && list ? (
                    <SortableHeadCell
                      key={column.field}
                      field={column.field}
                      activeField={list.sort}
                      activeOrder={list.order}
                      onSort={list.toggleSort}
                      align={column.align ?? 'left'}
                      sx={column.width === undefined ? undefined : { width: column.width }}
                    >
                      {column.label}
                    </SortableHeadCell>
                  ) : (
                    <TableCell
                      key={column.field}
                      align={column.align ?? 'left'}
                      sx={column.width === undefined ? undefined : { width: column.width }}
                    >
                      {column.label}
                    </TableCell>
                  ),
                )}
                {actions && <TableCell sx={{ width: actionsWidth }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={rowKey(row)} hover>
                  {columns.map((column) => (
                    <TableCell key={column.field} align={column.align ?? 'left'}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="right">
                      <RowActions actions={actions(row)} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {page && list && (
            <TablePager
              page={page}
              current={list.page}
              onPageChange={list.setPage}
              onLimitChange={list.setLimit}
            />
          )}
        </>
      )}
    </Paper>
  );
}
